import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography} from 'views/components';
import {MarkdownViewer, translate} from 'views/module';

import {getMessageTitle} from '../../communicationUtils';

import displayResource from 'utils/miscellaneous/display_resource';
import parseSesEmail from 'utils/miscellaneous/email/parse_ses_email';
import moment from 'moment';

const emptyEmail = {
  from: '',
  to: '',
  subject: '',
  text: '',
};

const EmailMessage = (props) => {
  const {
    message,
  } = props;
  const [parsedEmail, setParsedEmail] = React.useState(emptyEmail);

  const emailBlob = message?.body?.blob || '';
  const shouldParseEmail = message?.body?.editorType !== 'MARKDOWN';
  const createdAtMoment = moment(message.createdAt);
  const submittedBy = displayResource(message.submittedBy);

  React.useEffect(() => {
    let isActive = true;

    if (!shouldParseEmail) {
      setParsedEmail(emptyEmail);
      return () => {
        isActive = false;
      };
    }

    setParsedEmail(emptyEmail);

    parseSesEmail(emailBlob).then((value) => {
      if (isActive) {
        setParsedEmail(value || emptyEmail);
      }
    });

    return () => {
      isActive = false;
    };
  }, [emailBlob, shouldParseEmail]);

  const messageBody = React.useMemo(() => {
    if (shouldParseEmail) {
      return parsedEmail.text || parsedEmail.subject || '';
    }

    return message?.body?.blob || '';
  }, [message?.body?.blob, parsedEmail.subject, parsedEmail.text, shouldParseEmail]);
  const metadata = [
    shouldParseEmail && parsedEmail.from,
    submittedBy,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        border: '1px solid #ECECF1',
        borderRadius: '9px',
        backgroundColor: '#FFFFFF',
        p: '14px 15px',
      }}
    >
      <Stack sx={{gap: 0.85}}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: '#050A14',
            }}
          >
            {getMessageTitle(message.messageType)}
          </Typography>
          <Typography
            sx={{
              flexShrink: 0,
              fontSize: '11px',
              fontWeight: 400,
              color: '#9A9BAE',
            }}
          >
            {createdAtMoment.isValid() ? createdAtMoment.format('MMM D, h:mm A') : ''}
          </Typography>
        </Box>
        {
          shouldParseEmail && parsedEmail.subject && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                color: '#050A14',
                wordBreak: 'break-word',
              }}
            >
              {parsedEmail.subject}
            </Typography>
          )
        }
        {
          metadata.length > 0 && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: '#9A9BAE',
                wordBreak: 'break-word',
              }}
            >
              {metadata.join(' - ')}
            </Typography>
          )
        }
        <Box
          sx={{
            mt: 0.75,
            pt: 1.25,
            borderTop: '1px solid #ECECF1',
            fontSize: '12px',
            lineHeight: '18px',
            color: '#050A14',
            wordBreak: 'break-word',
          }}
        >
          <MarkdownViewer value={messageBody || translate('case.Communication.EmptyMessageBody')} truncate />
        </Box>
      </Stack>
    </Box>
  );
};

EmailMessage.propTypes = {
  message: PropTypes.object.isRequired,
};

export default React.memo(EmailMessage);
